import React from "react";
import { Box, Typography, Grid2 } from "@mui/material";

import sunsetImg from "../../Images/Baby_Bird_Photos/sunset.jpg";
import vetCare from "../../Images/Baby_Bird_Photos/vet_care.jpg";
import eggs from "../../Images/Baby_Bird_Photos/eggs.jpeg";

const birdPhotos = [
	require("../../Images/Baby_Bird_Photos/baby_ducks.jpeg"),
	require("../../Images/Baby_Bird_Photos/chickens.jpeg"),
	require("../../Images/Baby_Bird_Photos/gb.JPG"),
];

export default function AboutOurFlock() {
	return (
		<Box sx={{ p: 2, maxWidth: 900, margin: "0 auto" }}>
			<Box sx={{ mb: 2 }}>
				<Typography variant="h4" sx={{ mb: 2 }}>
					About Our Flock
				</Typography>
				<Typography variant="body1" gutterBottom>
					Our birds are raised with the utmost care, given high-quality feed,
					and provided with a clean, safe, and enriching environment. We believe
					happy, healthy birds lay the best eggs!
				</Typography>
				<Typography variant="body1" gutterBottom>
					We use premium feed from Purina and Mazuri, and our flock enjoys
					plenty of space to roam and forage. We are committed to ethical,
					sustainable, and humane animal care.
				</Typography>
			</Box>

			<Box sx={{ mb: 4 }}>
				<Typography variant="h5" sx={{ mb: 2 }}>
					Our Values
				</Typography>
				<Typography variant="body1" gutterBottom>
					We believe in raising egg-laying poultry and waterfowl in an ethical
					and empathetic manner. Raising our birds to live fulfilling, healthy,
					and enriched lives is one of our top priorities.
				</Typography>
				<Typography variant="body1" gutterBottom>
					We will always choose what we feel is best for our birds, especially
					when it comes to feed, housing requirements, and veterinary care.
				</Typography>
				<Box
					display="flex"
					flexDirection={{ xs: "column", sm: "row" }}
					alignItems="center"
					gap={2}
					mb={2}
					mt={1}
				>
					<img
						src={sunsetImg}
						alt="Gosell Gardens sunset"
						style={{
							width: "100%",
							maxWidth: 320,
							borderRadius: 12,
							objectFit: "cover",
							boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
						}}
						loading="lazy"
					/>
					<img
						src={birdPhotos[0]}
						alt="Baby ducks at Gosell Gardens"
						style={{
							width: "100%",
							maxWidth: 320,
							borderRadius: 12,
							objectFit: "cover",
							boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
						}}
						loading="lazy"
					/>
				</Box>
			</Box>

			<Box sx={{ mb: 4 }}>
				<Typography variant="h5" sx={{ mb: 2 }}>
					Our Care
				</Typography>
				<Typography variant="body1" gutterBottom>
					We believe that providing birds with a healthy start to life leads to
					strong, hardy, and healthy laying hens. Our chicks are vaccinated for
					both coccidiosis and Marek’s disease, both of which can quickly
					devastate an entire flock.
				</Typography>
				<Typography variant="body1" gutterBottom>
					Our chicks and ducklings are fed appropriate grower feed and are
					slowly introduced to the flock once their flight feathers appear,
					allowing them the chance to learn appropriate social cues and develop
					strong flock relationships. We also hold strong biosecurity standards
					to prevent morbidity and mortality. Our birds do not free range, as
					this increases the risk of infectious disease spread between wildlife
					and domestic flocks.
				</Typography>
				<Typography variant="body1" gutterBottom>
					We designate specific boots and clothes for barn chores, minimizing
					the risk of spreading disease to our personal animals. We always wash
					our hands before and after handling our flock members, their eggs, and
					any tools used to clean or maintain their coop. While our birds do not
					free range, we still strive to provide enrichment and entertainment
					activities by honing in on behaviors that they would naturally display
					were they not domesticated.
				</Typography>
				<Typography variant="body1" gutterBottom>
					Our chickens are provided with multiple climbing structures, roost
					bars, and even a chicken swing! To keep feathers healthy and free of
					external parasites, they also have deep dust baths, which make for
					some pretty cute communal dust bathing.
				</Typography>
				<Typography variant="body1" gutterBottom>
					Of course, our ducks enjoy swimming in their pool during the warmer
					months, which we often throw treats into so that they can practice
					hunting and diving for food. During the cold winter months, we provide
					our ducks with home-made foraging puzzle toys, which they love to
					shred once they are done with it!
				</Typography>
				<Box
					display="flex"
					flexDirection={{ xs: "column", sm: "row" }}
					alignItems="center"
					gap={2}
					mb={2}
					mt={1}
				>
					<img
						src={birdPhotos[2]}
						alt="Gosell Gardens birds"
						style={{
							width: "100%",
							maxWidth: 400,
							borderRadius: 12,
							objectFit: "cover",
							boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
						}}
						loading="lazy"
					/>
				</Box>
			</Box>

			<Box sx={{ mb: 4 }}>
				<Typography variant="h5" sx={{ mb: 2 }}>
					Our Feed
				</Typography>
				<Grid2
					container
					spacing={2}
					alignItems="center"
					justifyContent="center"
				>
					<Grid2 item xs={12} md={6}>
						<img
							src={eggs}
							alt="Fresh eggs"
							style={{
								width: "100%",
								maxWidth: 400,
								borderRadius: 12,
								objectFit: "cover",
								boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
								display: "block",
								margin: "0 auto",
							}}
						loading="lazy"
						/>
						<Typography variant="caption" display="block" textAlign="center">
							Fresh eggs from our happy flock!
						</Typography>
					</Grid2>
					<Grid2 item xs={12} md={6}>
						<Typography variant="body1" gutterBottom>
							Nutrition is one of the most important aspects of raising
							production animals; hence, we believe in food backed by science.
						</Typography>
						<Typography variant="body1" gutterBottom>
							Our chickens have access to free-choice Purina Layer Pellets,
							clean water, and oyster shells, while our ducks have access to
							free-choice Mazuri Layer Pellets and clean water.
						</Typography>
						<Typography variant="body1" gutterBottom>
							We have tried multiple different feeds and supplements, but none
							have compared to these brands in terms of health, egg size, and
							nutritional requirements for laying hens. These feeds are highly
							palatable and easily digestible, resulting in improved feed
							conversion ratios.
						</Typography>
						<Typography variant="body1" gutterBottom>
							Additionally, we trust that these feeds provide the appropriate
							nutrients and supplements required to maintain healthy digestive
							systems, feathers, and egg laying.
						</Typography>
						<Typography variant="body1" gutterBottom>
							As a result, our eggs have thick shells, bright yellow yolks, and
							healthy blooms that contribute to vibrant color variations.
						</Typography>
					</Grid2>
				</Grid2>
			</Box>

			<Box sx={{ mb: 4 }}>
				<Typography variant="h5" sx={{ mb: 2 }}>
					Veterinary Care
				</Typography>
				<Grid2
					container
					spacing={2}
					alignItems="center"
					justifyContent="center"
				>
					<Grid2 item xs={12} md={6}>
						<Box mb={2}>
							<img
								src={vetCare}
								alt="Veterinary Care"
								style={{
									width: "100%",
									maxWidth: 400,
									borderRadius: 12,
									objectFit: "cover",
									boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
									display: "block",
									margin: "0 auto",
								}}
						loading="lazy"
							/>
							<Typography variant="caption" display="block" textAlign="center">
								One of our birds getting treatment for a foot ailment.
							</Typography>
						</Box>
					</Grid2>
					<Grid2 item xs={12} md={6}>
						<Typography variant="body1" gutterBottom>
							Let’s face it: Owning animals requires veterinary visits. While
							not all of our ladies have names, we strongly believe that
							individualized care is necessary for healthy, happy birds.
						</Typography>
						<Typography variant="body1" gutterBottom>
							Our ladies are checked on twice daily, and we do our best to have
							eyes on every bird during these checks. Any individuals displaying
							out-of-the-ordinary behaviors will receive an immediate physical
							exam, and we try our best to treat ailments sustainably and
							ethically.
						</Typography>
						<Typography variant="body1" gutterBottom>
							We have strong, established relationships with multiple poultry
							veterinarians with the intention of maintaining a healthy flock.
						</Typography>
						<Typography variant="body1" gutterBottom>
							Illnesses that we do not feel comfortable managing at home will
							always be diverted to the care of a DVM. While we reserve the
							right to use systemic medications such as antibiotics and
							analgesics within our flock, we are believers in reducing the risk
							of antibiotic resistance.
						</Typography>
						<Typography variant="body1" gutterBottom>
							We will always guarantee responsible medication management and
							will maintain appropriate meat and egg withholding periods.
						</Typography>
						<Typography variant="body1" gutterBottom>
							This means that any sick birds requiring oral or injectable
							medications will be separated from our laying flock, and eggs from
							these birds will always be discarded appropriately.
						</Typography>
					</Grid2>
				</Grid2>
			</Box>
		</Box>
	);
}
